// src/modals/MallPriceEditDialog.jsx
import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Switch,
  Stack,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { tokenService } from "src/services";
import Services from "src/services/Services";

const VEHICLE_TABS = [
  { key: "pet-car", label: "Evcil Hayvan Aracı" },
  { key: "kid-car", label: "Oyuncaklı Çocuk Aracı" },
  { key: "gift", label: "Hediye Paketi" },
];

const VEHICLE_LABELS = {
  "pet-car": "Evcil Hayvan Aracı",
  "kid-car": "Oyuncaklı Çocuk Aracı",
  gift: "Hediye Paketi",
};

const HOURS = ["1", "2", "4", "6", "8", "12", "1 Gün"];
const CURRENCIES = ["TL", "USD", "EUR"];

const makeRow = ({ mallName, vehicleKey, duration, price, currency }) => ({
  mallName,
  vehicleKey,
  vehicleType: VEHICLE_LABELS[vehicleKey] ?? vehicleKey,
  duration,
  price: Number(price ?? 0),
  currency,
});

const mapCurrencyFromAPI = (c) => (c === "TRY" ? "TL" : c || "TL");
const mapCurrencyToAPI = (c) => (c === "TL" ? "TRY" : c || "TRY");

const toRowsFromInventoryPrice = (inventoryPrice, mallName) => {
  if (!inventoryPrice || typeof inventoryPrice !== "object") return [];
  const rows = [];

  Object.entries(inventoryPrice).forEach(([vehicleKey, val]) => {
    if (!val || typeof val !== "object") return;

    // --- Yeni şema #1: daily + hourly[] + status (örnek verdiğin format) ---
    if (Array.isArray(val.hourly) || val.daily) {
      if (Array.isArray(val.hourly)) {
        val.hourly.forEach((h) => {
          if (!h) return;
          rows.push(
            makeRow({
              mallName,
              vehicleKey,
              duration: String(h.hour ?? h.duration ?? ""),
              price: h.price ?? 0,
              currency: mapCurrencyFromAPI(h.currency),
            })
          );
        });
      }

      if (val.daily) {
        rows.push(
          makeRow({
            mallName,
            vehicleKey,
            duration: "1 Gün",
            price: val.daily.price ?? 0,
            currency: mapCurrencyFromAPI(val.daily.currency),
          })
        );
      }

      return;
    }

    // --- Gift yeni/eski şema: { price, currency, status } veya { price, currency } ---
    if (vehicleKey === "gift") {
      // price tanımlı DEĞİLSE hiç satır ekleme
      if (typeof val.price === "undefined") return;

      rows.push(
        makeRow({
          mallName,
          vehicleKey,
          duration: "-",
          price: val.price ?? 0,
          currency: mapCurrencyFromAPI(val.currency),
        })
      );
      return;
    }

    // --- Eski "prices" objesi şeması ---
    if (val.prices && typeof val.prices === "object") {
      Object.entries(val.prices).forEach(([dur, p]) => {
        rows.push(
          makeRow({
            mallName,
            vehicleKey,
            duration: dur,
            price: p?.price ?? 0,
            currency: mapCurrencyFromAPI(p?.currency),
          })
        );
      });
      return;
    }

    // --- En eski şema – hour / daily alanları ---
    if (val.hour) {
      rows.push(
        makeRow({
          mallName,
          vehicleKey,
          duration: "1",
          price: val.hour.price ?? 0,
          currency: mapCurrencyFromAPI(val.hour.currency),
        })
      );
    }
    if (val.daily) {
      rows.push(
        makeRow({
          mallName,
          vehicleKey,
          duration: "1 Gün",
          price: val.daily.price ?? 0,
          currency: mapCurrencyFromAPI(val.daily.currency),
        })
      );
    }
  });

  return rows;
};

// Barcode helper (0000001, 0000002, ...)
const padBarcode = (num) => num.toString().padStart(7, "0");

/**
 * vehicleInventory payload'ını üretir.
 *
 * Kurallar:
 * - pet-car & kid-car dışında kalan tipler (örn: gift) olduğu gibi korunur.
 * - Yeni sayı > mevcut sayı:
 *    * Mevcut item'lar aynen korunur (status dahil).
 *    * Üzerine (mevcutCount+1 .. yeniSayı) arası barcode'lar available olarak eklenir.
 * - Yeni sayı < mevcut sayı:
 *    * 1..yeniSayı arası barcode'lar yeniden oluşturulur, hepsi { status: "available" }.
 * - Yeni sayı === mevcut sayı:
 *    * Hiç dokunulmaz, mevcut liste olduğu gibi kalır.
 */
const buildVehicleInventoryPayload = (currentInventory, vehicleCounts) => {
  const next = { ...(currentInventory || {}) };
  const editableKeys = ["pet-car", "kid-car"];

  editableKeys.forEach((key) => {
    const desiredStr = vehicleCounts?.[key] ?? "0";
    const desired = parseInt(desiredStr, 10);
    if (Number.isNaN(desired) || desired < 0) return;

    const existingList = Array.isArray(currentInventory?.[key])
      ? [...currentInventory[key]]
      : [];
    const current = existingList.length;

    if (desired === 0) {
      next[key] = [];
      return;
    }

    if (desired > current) {
      // Mevcutları koru, üzerine yeni available araçlar ekle
      const newItems = [];
      for (let i = current + 1; i <= desired; i += 1) {
        newItems.push({
          barcode: padBarcode(i),
          status: "available",
        });
      }
      next[key] = [...existingList, ...newItems];
    } else if (desired < current) {
      // Tamamen sıfırla, 1..desired arası barcode ile hepsi available
      const newItems = [];
      for (let i = 1; i <= desired; i += 1) {
        newItems.push({
          barcode: padBarcode(i),
          status: "available",
        });
      }
      next[key] = newItems;
    } else {
      // Eşit ise liste aynen kalır
      next[key] = existingList;
    }
  });

  return next;
};

export default function MallPriceEditDialog({
  open,
  onClose,
  mallID,
  mallName = "AVM",
  onSubmit,
}) {
  const user = tokenService.getUser();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [vehicleTabIndex, setVehicleTabIndex] = React.useState(0);
  const selectedVehicleKey =
    VEHICLE_TABS[vehicleTabIndex]?.key ?? VEHICLE_TABS[0].key;
  const isGift = selectedVehicleKey === "gift";

  const [rows, setRows] = React.useState([]);
  const [tabStatuses, setTabStatuses] = React.useState({
    "pet-car": true,
    "kid-car": true,
    gift: true,
  });

  // Yeni: Araç envanteri (pet-car, kid-car vs.)
  const [vehicleInventory, setVehicleInventory] = React.useState({});
  // Yeni: pet-car ve kid-car için araç sayısı input state
  const [vehicleCounts, setVehicleCounts] = React.useState({
    "pet-car": "0",
    "kid-car": "0",
  });

  const [duration, setDuration] = React.useState("");
  const [currency, setCurrency] = React.useState(CURRENCIES[0]);
  const [price, setPrice] = React.useState("");

  React.useEffect(() => {
    const token = user?.token || localStorage.getItem("token");
    if (!open || !mallID || !token) return;

    (async () => {
      try {
        console.log(
          "[MallPriceEditDialog] GetMall -> request",
          JSON.stringify({ mallID }, null, 2)
        );
        const res = await Services.GetMall(token, mallID);
        console.log(
          "[MallPriceEditDialog] GetMall -> raw response",
          res?.data
        );

        const result = res?.data?.result;
        const invPrice = result?.vehicleInventoryWithPrice;
        const mappedRows = toRowsFromInventoryPrice(
          invPrice,
          result?.title || mallName
        );
        setRows(mappedRows);

        // Yeni: araç envanteri
        const inv = result?.vehicleInventory || {};
        setVehicleInventory(inv);

        // Yeni: initial count'lar (yoksa 0)
        setVehicleCounts({
          "pet-car": String(
            Array.isArray(inv["pet-car"]) ? inv["pet-car"].length : 0
          ),
          "kid-car": String(
            Array.isArray(inv["kid-car"]) ? inv["kid-car"].length : 0
          ),
        });

        const nextStatuses = { ...tabStatuses };
        VEHICLE_TABS.forEach(({ key }) => {
          const apiObj = invPrice?.[key];
          if (apiObj && typeof apiObj === "object" && "status" in apiObj) {
            nextStatuses[key] = !!apiObj.status;
          } else {
            nextStatuses[key] = mappedRows.some((r) => r.vehicleKey === key);
          }
        });
        setTabStatuses(nextStatuses);
      } catch (err) {
        console.error("[MallPriceEditDialog] GetMall -> error", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mallID, user?.token, mallName]);

  React.useEffect(() => {
    if (open && mallName) {
      setRows((prev) => prev.map((r) => ({ ...r, mallName })));
    }
  }, [open, mallName]);

  // Sekme değiştiğinde gift’te saat seçimini devre dışı bırakmak için form state’i normalize et
  React.useEffect(() => {
    if (isGift) {
      setDuration("-");
    } else if (duration === "-") {
      setDuration("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicleKey]);

  // Yeni: Araç sayısı input change handler (sadece sayı)
  const handleVehicleCountChange = (key) => (e) => {
    const val = e.target.value;
    if (val === "") {
      setVehicleCounts((prev) => ({ ...prev, [key]: "" }));
      return;
    }
    const numeric = val.replace(/\D/g, "");
    setVehicleCounts((prev) => ({ ...prev, [key]: numeric }));
  };

  const handleAdd = () => {
    // gift: sadece price zorunlu; diğerleri: duration + price zorunlu
    if (isGift) {
      if (!price) return;
    } else {
      if (!duration || !price) return;
    }

    const next = makeRow({
      mallName,
      vehicleKey: selectedVehicleKey,
      duration: isGift ? "-" : duration,
      price: Number(price),
      currency,
    });
    setRows((p) => [...p, next]);

    if (!isGift) setDuration("");
    setPrice("");
  };

  const removeRow = (idx) => {
    const filtered = rows.filter((r) => r.vehicleKey === selectedVehicleKey);
    const target = filtered[idx];
    let removed = false;
    setRows((prev) =>
      prev.filter((r) => {
        if (
          !removed &&
          r.vehicleKey === target.vehicleKey &&
          r.duration === target.duration &&
          r.price === target.price &&
          r.currency === target.currency
        ) {
          removed = true;
          return false;
        }
        return true;
      })
    );
  };

  const handleCancel = () => onClose?.();

  const handleUpdate = async () => {
    const token = user?.token || localStorage.getItem("token");
    if (!token) return;

    const vehicleInventoryWithPrice = buildVehicleInventoryWithPrice(
      rows,
      tabStatuses
    );

    // Yeni: vehicleInventory payload'ı
    const vehicleInventoryPayload = buildVehicleInventoryPayload(
      vehicleInventory,
      vehicleCounts
    );

    console.log(
      "[MallPriceEditDialog] UpdateMallInventory -> request",
      JSON.stringify({ mallID, vehicleInventoryWithPrice }, null, 2)
    );
    console.log(
      "[MallPriceEditDialog] UpdateMallVehicleInventory -> request",
      JSON.stringify({ mallID, vehicleInventory: vehicleInventoryPayload }, null, 2)
    );

    try {
      // Hem fiyatları hem araç envanterini güncelle
      const [invRes, priceRes] = await Promise.all([
        Services.UpdateMallVehicleInventory(
          token,
          mallID,
          vehicleInventoryPayload
        ),
        Services.UpdateMallInventory(token, mallID, vehicleInventoryWithPrice),
      ]);
      console.log(
        "[MallPriceEditDialog] UpdateMallVehicleInventory -> response",
        invRes?.data
      );
      console.log(
        "[MallPriceEditDialog] UpdateMallInventory -> response",
        priceRes?.data
      );
    } catch (err) {
      console.error("[MallPriceEditDialog] UpdateMall -> error", err);
    }

    onSubmit?.({
      mallID,
      mallName,
      vehicleInventoryWithPrice,
      vehicleInventory: vehicleInventoryPayload,
    });
    onClose?.();
  };

  // Seçili tab’daki satırlar
  const filteredRows = rows.filter((r) => r.vehicleKey === selectedVehicleKey);

  // Aynı saat (duration) birden fazla kez eklenmişse
  const obsoleteIndexes = React.useMemo(() => {
    const byDuration = {};
    filteredRows.forEach((r, idx) => {
      const durKey = r.duration || "-";
      if (!byDuration[durKey]) byDuration[durKey] = [];
      byDuration[durKey].push(idx);
    });

    const toBeObsolete = new Set();
    Object.values(byDuration).forEach((indices) => {
      if (indices.length > 1) {
        indices.slice(0, -1).forEach((i) => toBeObsolete.add(i));
      }
    });

    return toBeObsolete;
  }, [filteredRows]);

  // Yeni: Araç sayısı / tooltip için hesaplamalar
  const isVehicleCountEditable =
    selectedVehicleKey === "pet-car" || selectedVehicleKey === "kid-car";

  // Seçili araç tipindeki tüm araçlar "available" mı?
  const allVehiclesAvailableForSelected = React.useMemo(() => {
    if (!isVehicleCountEditable) return false;

    const list = Array.isArray(vehicleInventory?.[selectedVehicleKey])
      ? vehicleInventory[selectedVehicleKey]
      : [];

    // Envanter yoksa / boşsa, sayı değiştirilebilir kabul edelim
    if (list.length === 0) return true;

    return list.every(
      (item) =>
        !item?.status ||
        String(item.status).toLowerCase() === "available"
    );
  }, [isVehicleCountEditable, vehicleInventory, selectedVehicleKey]);

  const canEditVehicleCount =
    isVehicleCountEditable && allVehiclesAvailableForSelected;

  const currentInventoryCount = isVehicleCountEditable
    ? Array.isArray(vehicleInventory?.[selectedVehicleKey])
      ? vehicleInventory[selectedVehicleKey].length
      : 0
    : 0;

  const desiredCount = isVehicleCountEditable
    ? parseInt(vehicleCounts[selectedVehicleKey] || "0", 10) || 0
    : 0;

  const willResetStatuses =
    isVehicleCountEditable &&
    desiredCount < currentInventoryCount &&
    currentInventoryCount > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      aria-labelledby="mall-edit-title"
    >
      <DialogTitle
        id="mall-edit-title"
        sx={{ textAlign: "center", fontWeight: 700, pr: 7 }}
      >
        AVM Hizmetleri Fiyat Ayarları
        <IconButton
          aria-label="Kapat"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: "text.secondary", mb: 1.5, fontWeight: 600 }}
        >
          AVM Bilgileri
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="AVM Adı"
              value={mallName}
              disabled
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="AVM ID"
              value={mallID || "-"}
              disabled
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            variant="subtitle2"
            sx={{ color: "text.secondary", mb: 1.5, fontWeight: 600 }}
          >
            Fiyatlandırma
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {VEHICLE_LABELS[selectedVehicleKey]} Durumu
            </Typography>
            <Tooltip title="Bu anahtar yalnızca seçili sekmedeki araç tipini etkiler">
              <Switch
                checked={!!tabStatuses[selectedVehicleKey]}
                onChange={(e) =>
                  setTabStatuses((p) => ({
                    ...p,
                    [selectedVehicleKey]: !!e.target.checked,
                  }))
                }
              />
            </Tooltip>
          </Stack>
        </Stack>

        {/* Yeni: Araç sayısı alanı (sadece pet-car & kid-car) */}
        {isVehicleCountEditable && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              type="number"
              label={`${VEHICLE_LABELS[selectedVehicleKey]} Sayısı`}
              value={vehicleCounts[selectedVehicleKey]}
              onChange={handleVehicleCountChange(selectedVehicleKey)}
              inputProps={{ min: 0 }}
              disabled={!canEditVehicleCount}
            />
            <Tooltip
              title={
                !allVehiclesAvailableForSelected
                  ? `${VEHICLE_LABELS[selectedVehicleKey]} sayısını değiştirebilmek için bu araç tipindeki tüm araçların "Uygun" durumda olması gerekir. Kullanımda, arızalı veya kayıp araç varken sayı değiştirilemez.`
                  : willResetStatuses
                    ? `Bu sayıyı ${currentInventoryCount} → ${desiredCount} olarak düşürürseniz tüm ${VEHICLE_LABELS[selectedVehicleKey]} araçlarının durumu "available" olarak güncellenecek.`
                    : `${VEHICLE_LABELS[selectedVehicleKey]} envanter sayısını güncelleyin.`
              }
            >
              <InfoOutlinedIcon
                fontSize="small"
                color={
                  !allVehiclesAvailableForSelected
                    ? "warning"
                    : willResetStatuses
                      ? "error"
                      : "disabled"
                }
                sx={{ cursor: "help", mt: { xs: 0.5, sm: 0 } }}
              />
            </Tooltip>

            {willResetStatuses && allVehiclesAvailableForSelected && (
              <Typography
                variant="caption"
                color="error"
                sx={{ maxWidth: 360 }}
              >
                Dikkat: Sayıyı azaltırsanız bu araç tipindeki tüm araçların
                statüsü &quot;Uygun&quot; yapılacak.
              </Typography>
            )}

            {!willResetStatuses &&
              currentInventoryCount > 0 &&
              allVehiclesAvailableForSelected && (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  Mevcut envanter: {currentInventoryCount} araç
                </Typography>
              )}

            {/* Yeni uyarı: available olmayan araç varken sayı değiştirilemez */}
            {!allVehiclesAvailableForSelected && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ maxWidth: 420 }}
              >
                {VEHICLE_LABELS[selectedVehicleKey]} sayısını güncellemeden
                önce, bu araç tipindeki tüm araçların &quot;Uygun&quot;
                durumda olduğundan emin olun. Kullanımda, arızalı, kayıp veya
                devre dışı araçlar varken araç sayısı değiştirilemez.
              </Typography>
            )}
          </Stack>
        )}
        <Tabs
          value={vehicleTabIndex}
          onChange={(_, v) => setVehicleTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Araç Tipi Sekmeleri"
          sx={{ mb: 3 }}
        >
          {VEHICLE_TABS.map((t, i) => (
            <Tab
              key={t.key}
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{t.label}</span>
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      background: tabStatuses[t.key] ? "#16a34a" : "#9ca3af",
                      opacity: 0.9,
                    }}
                    aria-label={tabStatuses[t.key] ? "Aktif" : "Pasif"}
                    title={tabStatuses[t.key] ? "Aktif" : "Pasif"}
                  />
                </Stack>
              }
              id={`vehicle-tab-${i}`}
            />
          ))}
        </Tabs>

        {/* Ekle alanları */}
        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Seçili Araç Tipi"
              value={VEHICLE_LABELS[selectedVehicleKey]}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          {/* Hediye Paketi'nde saat seçimi yok */}
          {!isGift && (
            <Grid item xs={6} sm={3} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Saat</InputLabel>
                <Select
                  label="Saat"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  displayEmpty
                  renderValue={(val) => (val ? val : "Saat seçin")}
                >
                  {HOURS.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={6} sm={3} md={isGift ? 3.5 : 2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Para Birimi</InputLabel>
              <Select
                label="Para Birimi"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={isGift ? 3.5 : 2.5}>
            <TextField
              fullWidth
              size="small"
              label="Fiyat Bilgisi"
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid
            item
            xs={6}
            sm={3}
            md={1.5}
            sx={{ display: "flex", alignItems: "stretch" }}
          >
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={handleAdd}
              disabled={isGift ? !price : !duration || !price}
              sx={{ minWidth: 96 }}
            >
              Ekle
            </Button>
          </Grid>
        </Grid>

        {/* Tablo */}
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
          <Table size="small" aria-label="Fiyat Tablosu">
            <TableHead>
              <TableRow
                sx={{ "& th": { fontWeight: 700, color: "text.secondary" } }}
              >
                <TableCell>AVM ADI</TableCell>
                <TableCell>ARAÇ TİPİ</TableCell>
                <TableCell>SAAT</TableCell>
                <TableCell>FİYAT BİLGİSİ</TableCell>
                <TableCell>PARA BİRİMİ</TableCell>
                <TableCell align="center">KALDIR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((r, i) => {
                const isObsolete = obsoleteIndexes.has(i);

                return (
                  <TableRow
                    key={`${r.vehicleKey}-${r.duration}-${i}`}
                    sx={
                      isObsolete
                        ? {
                          "& td": {
                            color: "error.main",
                            fontWeight: 500,
                            backgroundColor: "rgba(239,68,68,0.04)",
                          },
                        }
                        : undefined
                    }
                  >
                    <TableCell>{r.mallName}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {r.vehicleType}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {r.duration}
                    </TableCell>
                    <TableCell>{r.price}</TableCell>
                    <TableCell>{r.currency}</TableCell>
                    <TableCell align="center">
                      {!isObsolete && (
                        <IconButton
                          aria-label="Kaldır"
                          onClick={() => removeRow(i)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: "text.secondary" }}>
                    Bu araç tipi için henüz fiyat eklenmedi.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3, mb: 1 }}
        >
          <Button variant="outlined" color="error" onClick={handleCancel}>
            Vazgeç
          </Button>
          <Button variant="contained" onClick={handleUpdate}>
            Güncelle
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// Fiyat payload helper (en altta, eski kodun fonksiyonu burada olabilir)
function buildVehicleInventoryWithPrice(rows, tabStatuses) {
  const payload = {};

  // 1) Araç tiplerine göre grupla
  const byVehicle = {};
  rows.forEach((r) => {
    if (!r || !r.vehicleKey) return;
    const vKey = r.vehicleKey;
    if (!byVehicle[vKey]) byVehicle[vKey] = [];
    byVehicle[vKey].push(r);
  });

  // 2) Sekmelerde tanımlı ama satırı olmayan tipler de payload'a girsin
  VEHICLE_TABS.forEach(({ key }) => {
    if (!byVehicle[key]) byVehicle[key] = [];
  });

  // 3) Her araç tipi için hedef API formatını üret
  Object.keys(byVehicle).forEach((vKey) => {
    const list = byVehicle[vKey];
    const status =
      tabStatuses && Object.prototype.hasOwnProperty.call(tabStatuses, vKey)
        ? !!tabStatuses[vKey]
        : true;

    // --- Gift: { price, currency, status } ---
    if (vKey === "gift") {
      const last = list[list.length - 1];
      if (last) {
        payload[vKey] = {
          price: Number(last.price ?? 0),
          currency: mapCurrencyToAPI(last.currency),
          status,
        };
      } else {
        payload[vKey] = { status };
      }
      return;
    }

    // --- Diğer araç tipleri: { daily, hourly: [], status } ---
    const vehPayload = {
      status,
    };
    const hourly = [];

    list.forEach((r) => {
      const rawDur = r.duration;
      const price = Number(r.price ?? 0);
      const currency = mapCurrencyToAPI(r.currency);

      if (!rawDur) return;

      // "1 Gün" → daily
      if (String(rawDur).toLowerCase().includes("gün")) {
        vehPayload.daily = {
          price,
          currency,
        };
      } else {
        // Diğerleri → hourly
        hourly.push({
          hour: String(rawDur),
          price,
          currency,
        });
      }
    });

    if (hourly.length > 0) {
      vehPayload.hourly = hourly;
    }

    payload[vKey] = vehPayload;
  });

  return payload;
}